'use client'

import { motion, useReducedMotion } from 'motion/react'
import { cn } from '../../../../lib/cn'

export interface GridDiagonalFullProps {
  /**
   * Enable entrance animation
   * @default true
   */
  animate?: boolean

  /**
   * Animation delay in seconds (allows hero content to load first)
   * @default 0.5
   */
  animationDelay?: number

  className?: string
}

/**
 * GridDiagonalFull - Diagonal grid pattern covering the full area
 *
 * 45-degree crossed lines creating a diamond/argyle pattern
 * with no fade effect. The pattern covers the entire container uniformly.
 *
 * @example
 * ```tsx
 * <section className="relative">
 *   <GridDiagonalFull />
 *   <div className="relative z-10">Content here</div>
 * </section>
 * ```
 */
export function GridDiagonalFull({
  animate = true,
  animationDelay = 0.5,
  className,
}: GridDiagonalFullProps = {}) {
  const prefersReducedMotion = useReducedMotion()

  return (
    <motion.div
      className={cn('absolute inset-0 z-0', className)}
      style={{
        backgroundImage: `
          repeating-linear-gradient(45deg, color-mix(in srgb, var(--border), transparent 50%) 0px, color-mix(in srgb, var(--border), transparent 50%) 1px, transparent 1px, transparent 20px),
          repeating-linear-gradient(-45deg, color-mix(in srgb, var(--border), transparent 50%) 0px, color-mix(in srgb, var(--border), transparent 50%) 1px, transparent 1px, transparent 20px)
        `,
      }}
      initial={animate ? { opacity: 0 } : false}
      animate={animate ? { opacity: 1 } : false}
      transition={{
        duration: prefersReducedMotion ? 0.3 : 1.2,
        delay: animate ? (prefersReducedMotion ? 0 : animationDelay) : 0,
        ease: [0.16, 1, 0.3, 1],
      }}
    />
  )
}
